import React from "react";
import Box from "@mui/material/Box";
import styled from "styled-components";
import { useMediaQuery } from "@mui/material";
import { useLocation } from "react-router-dom";
import { CircleFlag } from "react-circle-flags";
import Typography from "@mui/material/Typography";
import { colors, customBreakpointsMax } from "app/theme";
import { PageHeaderProps } from "app/components/page-header/data";
import { useFavouritesUtils } from "app/hooks/useFavouritesUtils";
import {
  BookmarkCheckedIconButton,
  BookmarkIconButton,
} from "../bookmark-button";

const Container = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const PageHeader: React.FC<PageHeaderProps> = (
  props: PageHeaderProps
) => {
  const location = useLocation();
  const mobile = useMediaQuery("(max-width: 743px)");
  const tablet = useMediaQuery("(max-width: 1439px)");
  const { favourites, onFavouriteButtonClick } = useFavouritesUtils();

  const icon = React.useMemo(() => {
    if (props.locationISO2) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <CircleFlag
            width="60"
            height="60"
            countryCode={props.locationISO2.toLowerCase()}
          />
        </Box>
      );
    }
    if (props.icon) {
      return props.icon;
    }
    return null;
  }, [props.icon, props.locationISO2]);

  const getTitleFontSizeWithoutSubtitle = () => {
    if (props.title.length < 90) {
      return { xs: "18px", md: "36px", lg: "40px" };
    }
    if (props.title.length < 150) {
      return { xs: "16px", md: "32px", lg: "36px" };
    }
    return { xs: "14px", md: "20px", lg: "24px" };
  };

  const handleFavouriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!props.favourite) return;
    onFavouriteButtonClick({
      name: props.favourite?.name,
      path: location.pathname + location.search,
      itemId:
        props.favourite?.type === "activity" && props.favourite?.id
          ? props.favourite?.id
          : location.pathname + location.search,
      type: props.favourite?.type,
    });
  };

  const title = React.useMemo(() => {
    return (
      <Typography
        margin={0}
        variant="h1"
        fontSize={
          props.subtitle
            ? { xs: "24px", md: "36px", lg: "40px" }
            : getTitleFontSizeWithoutSubtitle()
        }
        color={colors.primary.white}
        sx={{
          lineClamp: mobile ? "5" : props.subtitle ? "2" : "3",
          overflow: "hidden",
          WebkitLineClamp: mobile ? "5" : props.subtitle ? "2" : "3",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
        }}
      >
        {props.title}
      </Typography>
    );
  }, [props.title, props.subtitle, mobile]);

  const subtitle = React.useMemo(() => {
    if (tablet) return null;
    if (!props.subtitle) return null;
    return (
      <Typography
        margin={0}
        fontSize="18px"
        fontWeight="400"
        color={colors.primary.white}
        lineHeight={1.235}
      >
        {props.subtitle}
      </Typography>
    );
  }, [props.subtitle, tablet]);

  const isFavourite = React.useMemo(() => {
    if (!props.favourite) return false;
    return favourites.some(
      (favourite) =>
        favourite.itemId ===
          (props.favourite?.id ?? location.pathname + location.search) &&
        favourite.type === props.favourite?.type
    );
  }, [favourites, props.favourite, location.pathname, location.search]);

  const content = React.useMemo(() => {
    return (
      <Container>
        <Container>
          {icon}
          <Box
            sx={{
              gap: "5px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              maxWidth: "calc(100% - 60px)",
              marginLeft: { xs: "10px", md: "20px", lg: 0 },
            }}
          >
            {title}
            {subtitle}
          </Box>
        </Container>
        {props.favourite && (
          <React.Fragment>
            {isFavourite ? (
              <BookmarkCheckedIconButton
                iconSize="40px"
                onClick={handleFavouriteClick}
                iconColor={colors.primary.white}
              />
            ) : (
              <BookmarkIconButton
                iconSize="40px"
                onClick={handleFavouriteClick}
                iconColor={colors.primary.white}
              />
            )}
          </React.Fragment>
        )}
      </Container>
    );
  }, [
    icon,
    title,
    tablet,
    subtitle,
    isFavourite,
    props.variant,
    props.favourite,
  ]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        padding: {
          xs: "10px",
          md: "20px",
        },
        height: "130px",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        background:
          props.variant === "explorer"
            ? colors.primary.blue
            : colors.primary.green,
        [customBreakpointsMax.tablet]: {
          svg: {
            width: "80px",
            height: "80px",
          },
          height: "120px",
        },
        [customBreakpointsMax.mobile]: {
          svg: {
            width: "50px",
            height: "50px",
          },
          minHeight: "57px",
        },
      }}
      data-cy="page-header"
    >
      {content}
    </Box>
  );
};
